import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { submitEnquiry } from "../lib/api";

const CATEGORIES = [
  "Food Service", "Chemical Hygiene", "Cleaning Equipment", "Industrial Packaging",
  "Paper Hygiene", "Plastic Packaging", "Hand Protection", "Protective Safety",
  "Adhesive Tape", "Waste Disposal", "Office Supplies", "General enquiry",
];

const inputCls =
  "w-full bg-white border border-vr-border rounded-lg px-4 py-3 text-vr-ink placeholder:text-vr-dim/60 focus:outline-none focus:border-vr-brand focus:ring-2 focus:ring-vr-brand/15 transition-all";

export const EnquiryForm = ({ compact = false, initialSku = "" }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", category: "", sku: initialSku,
    message: initialSku ? `Please send bulk pricing and availability for SKU ${initialSku}.` : "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, kind: initialSku ? "bulk-pricing" : "enquiry" });
      toast.success("Enquiry sent — our team will respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", category: "", sku: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      data-testid="enquiry-form"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
    >
      <div>
        <label className="block text-sm font-medium text-vr-ink mb-2">Name *</label>
        <input data-testid="enquiry-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
      </div>
      <div>
        <label className="block text-sm font-medium text-vr-ink mb-2">Email *</label>
        <input data-testid="enquiry-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.co.nz" />
      </div>
      <div>
        <label className="block text-sm font-medium text-vr-ink mb-2">Phone</label>
        <input data-testid="enquiry-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+64 ..." />
      </div>
      <div>
        <label className="block text-sm font-medium text-vr-ink mb-2">Company</label>
        <input data-testid="enquiry-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-vr-ink mb-2">Product interest</label>
        <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
          <SelectTrigger data-testid="enquiry-category" className={`${inputCls} h-auto`}>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-vr-ink mb-2">Message *</label>
        <textarea data-testid="enquiry-message" rows={compact ? 4 : 5} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us what you need..." />
      </div>
      <div className="md:col-span-2">
        <button
          type="submit"
          data-testid="enquiry-submit"
          disabled={loading}
          className="group inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : null}
          {loading ? "Sending..." : "Send enquiry"}
          {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
        </button>
      </div>
    </motion.form>
  );
};

export const EnquirySection = () => (
  <section id="enquiry" data-testid="enquiry-section" className="py-20 md:py-28 bg-vr-bg2">
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <span className="text-xs tracking-[0.2em] uppercase text-vr-brand font-semibold">Get in touch</span>
        <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-3xl md:text-5xl text-vr-ink">
          Request a quote.
        </h2>
        <p className="mt-5 text-vr-dim max-w-md leading-relaxed">
          Tell us about your operation and requirements. We&apos;ll match you with the right products
          and reply within one business day.
        </p>
        <div className="mt-10 space-y-4 text-vr-ink">
          <p className="font-semibold">Prefer to talk?</p>
          <p className="text-vr-dim">Victor — Sales &amp; Marketing</p>
          <p className="text-vr-dim">Call <a href="tel:+64221598639" className="text-vr-brand font-medium">+64 22 159 8639</a></p>
          <p className="text-vr-dim">Email <a href="mailto:sales@vrpackaging.co.nz" className="text-vr-brand font-medium">sales@vrpackaging.co.nz</a></p>
        </div>
      </motion.div>
      <div className="bg-white rounded-3xl border border-vr-border shadow-sm p-6 md:p-8">
        <EnquiryForm />
      </div>
    </div>
  </section>
);
